import * as maplibregl from 'maplibre-gl';
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import { config } from '../config.js';

maplibregl.setWorkerUrl(workerUrl);

let map;
let markers = [];
let largeMap;
let largeMarkers = [];
let largeMapOptions = {};
let largeEditMarker = null;
let largeEditMoveHandler = null;
let cancelLargeMapPickHandler = null;

// Warstwa SVG jest niezależna od stylu MapLibre. Dzięki temu trasa nie może
// zostać przykryta przez kafelki mapy ani zniknąć po przeładowaniu stylu.
const routeOverlays = new WeakMap();
const missingGeometryWarnings = new Set();

const emptyStyle = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
};

export function initMap() {
  map = createMap('map', { center: [19.2, 52.0], zoom: 5.2 });
  map.addControl(new maplibregl.NavigationControl(), 'top-right');
  return map;
}

export function drawRoute(route) {
  if (!map) return;
  map.resize();
  renderRoute(map, route, markers, 'route-main');
}

export function fitRoute(points = []) {
  const overlay = routeOverlays.get(map);
  fitBoundsOnMap(map, points, overlay?.geometry || null, {
    padding: 55,
    maxZoom: 12,
    duration: 500,
  });
}

export async function showLargeMapPreview(route, options = {}) {
  largeMapOptions = { ...largeMapOptions, ...options };

  if (!largeMap) {
    largeMap = createMap('largeMap', { center: [19.2, 49.2], zoom: 6 });
    largeMap.addControl(new maplibregl.NavigationControl(), 'top-right');
  }

  await waitForMapReady(largeMap);
  largeMap.resize();
  renderRoute(largeMap, route, largeMarkers, 'route-large');
  await waitForMapIdle(largeMap, 1800);
  redrawOverlay(largeMap);
}

export function showLargeMapBorderEditMarker(point, onMove) {
  if (!largeMap || !validCoordinate(point)) return;

  clearLargeMapBorderEditMarker();
  largeEditMoveHandler = typeof onMove === 'function' ? onMove : null;

  const element = document.createElement('div');
  element.className = 'map-border-edit-marker';
  const constraint = isRouteConstraint(point);
  element.title = constraint ? 'Przeciągnij punkt wymuszający przebieg całej trasy' : 'Przeciągnij punkt, przez który ma przebiegać cała trasa';
  element.innerHTML = `<span>${constraint ? 'P' : 'G'}</span>`;

  largeEditMarker = new maplibregl.Marker({
    element,
    draggable: true,
    anchor: 'center',
  })
    .setLngLat([Number(point.lon), Number(point.lat)])
    .addTo(largeMap);

  largeEditMarker.on('dragend', () => {
    const lngLat = largeEditMarker?.getLngLat();
    if (!lngLat || !largeEditMoveHandler) return;
    largeEditMoveHandler({ lon: lngLat.lng, lat: lngLat.lat });
  });
}

export function moveLargeMapBorderEditMarker(point) {
  if (!largeEditMarker || !validCoordinate(point)) return;
  largeEditMarker.setLngLat([Number(point.lon), Number(point.lat)]);
}

export function clearLargeMapBorderEditMarker() {
  cancelLargeMapPointPick();
  largeEditMarker?.remove();
  largeEditMarker = null;
  largeEditMoveHandler = null;
}

export function beginLargeMapPointPick(onPick) {
  if (!largeMap || typeof onPick !== 'function') return () => {};

  cancelLargeMapPointPick();
  const canvas = largeMap.getCanvas();
  canvas.style.cursor = 'crosshair';

  const handler = event => {
    cancelLargeMapPointPick();
    const point = { lon: event.lngLat.lng, lat: event.lngLat.lat };
    moveLargeMapBorderEditMarker(point);
    onPick(point);
  };

  largeMap.once('click', handler);
  cancelLargeMapPickHandler = () => {
    largeMap?.off('click', handler);
    if (canvas) canvas.style.cursor = '';
    cancelLargeMapPickHandler = null;
  };

  return cancelLargeMapPointPick;
}

export function cancelLargeMapPointPick() {
  const cancel = cancelLargeMapPickHandler;
  cancelLargeMapPickHandler = null;
  cancel?.();
}

export function captureMap() {
  if (!map) return '';

  try {
    const sourceCanvas = map.getCanvas();
    const width = sourceCanvas.width;
    const height = sourceCanvas.height;
    if (!width || !height) return '';

    const output = document.createElement('canvas');
    output.width = width;
    output.height = height;
    const context = output.getContext('2d');
    if (!context) return sourceCanvas.toDataURL('image/png');

    context.drawImage(sourceCanvas, 0, 0, width, height);

    // map.project() zwraca współrzędne w pikselach CSS, dlatego uwzględniamy
    // skalę między rozmiarem CSS a rzeczywistym rozmiarem canvasa.
    const cssWidth = sourceCanvas.clientWidth || width;
    const cssHeight = sourceCanvas.clientHeight || height;
    const scaleX = width / cssWidth;
    const scaleY = height / cssHeight;
    const overlay = routeOverlays.get(map);

    if (overlay?.geometry?.coordinates?.length >= 2) {
      drawCanvasRoute(context, map, overlay.geometry.coordinates, scaleX, scaleY);
    }
    if (overlay?.points?.length) {
      drawCanvasMarkers(context, map, overlay.points, scaleX, scaleY);
    }

    return output.toDataURL('image/png');
  } catch (error) {
    console.warn('[D24 MAP] Nie udało się złożyć zrzutu mapy:', error);
    try {
      return map.getCanvas().toDataURL('image/png');
    } catch {
      return '';
    }
  }
}

function createMap(container, { center, zoom }) {
  return new maplibregl.Map({
    container,
    style: config.mapStyleUrl || emptyStyle,
    center,
    zoom,
    preserveDrawingBuffer: true,
    attributionControl: true,
  });
}

function renderRoute(targetMap, route, markerStore, overlayId) {
  if (!targetMap) return;

  clearMarkers(markerStore);
  const points = routePoints(route);
  const geometry = resolveRoadGeometry(route, points);

  points.forEach((point, index) => {
    if (!validCoordinate(point)) return;

    const element = document.createElement('div');
    const routeConstraint = isRouteConstraint(point);
    const editableRoutePoint = targetMap === largeMap
      && (point.isBorder || routeConstraint)
      && typeof largeMapOptions.onBorderClick === 'function';
    element.className = `map-marker${point.isBorder ? ' border' : ''}${routeConstraint ? ' route-via' : ''}${editableRoutePoint ? ' editable-border' : ''}`;
    element.style.zIndex = '4';
    const markerLabel = point.isBorder ? 'G' : routeConstraint ? 'P' : index + 1;
    element.innerHTML = `<span>${markerLabel}</span>`;
    if (editableRoutePoint) {
      element.title = routeConstraint
        ? 'Kliknij, aby edytować punkt wymuszający przebieg trasy'
        : 'Kliknij, aby wymusić inny przebieg całej trasy';
      element.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        largeMapOptions.onBorderClick(point);
      });
    }

    const editHint = editableRoutePoint
      ? `<br><em>Kliknij znacznik ${routeConstraint ? 'P' : 'G'}, aby zmienić przebieg całej trasy.</em>`
      : '';
    const markerType = routeConstraint ? '<br><strong>Punkt wymuszający przebieg</strong>' : '';
    const marker = new maplibregl.Marker({ element })
      .setLngLat([Number(point.lon), Number(point.lat)])
      .setPopup(new maplibregl.Popup({ offset: 18 }).setHTML(
        `<strong>${escapeHtml(point.name)}</strong><br>${escapeHtml(point.countryName || point.countryCode || '')}${markerType}${editHint}`
      ))
      .addTo(targetMap);

    markerStore.push(marker);
  });

  const overlay = ensureRouteOverlay(targetMap, overlayId);
  overlay.geometry = geometry;
  overlay.points = points.filter(validCoordinate);

  const apply = () => {
    redrawOverlay(targetMap);
    fitBoundsOnMap(targetMap, points, geometry, {
      padding: 70,
      maxZoom: 12,
      duration: 0,
    });
    // fitBounds zmienia widok asynchronicznie. Ponowne rysowanie gwarantuje,
    // że ścieżka odpowiada końcowemu położeniu mapy.
    requestAnimationFrame(() => redrawOverlay(targetMap));
    setTimeout(() => redrawOverlay(targetMap), 120);
  };

  if (targetMap.loaded() || targetMap.isStyleLoaded()) apply();
  else targetMap.once('load', apply);
}

function ensureRouteOverlay(targetMap, overlayId) {
  const existing = routeOverlays.get(targetMap);
  if (existing) return existing;

  const container = targetMap.getContainer();
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.dataset.d24RouteOverlay = overlayId;
  svg.setAttribute('aria-hidden', 'true');
  Object.assign(svg.style, {
    position: 'absolute',
    inset: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    overflow: 'hidden',
    zIndex: '3',
  });

  const outline = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  outline.setAttribute('fill', 'none');
  outline.setAttribute('stroke', '#ffffff');
  outline.setAttribute('stroke-width', '10');
  outline.setAttribute('stroke-linecap', 'round');
  outline.setAttribute('stroke-linejoin', 'round');
  outline.setAttribute('opacity', '0.96');

  const line = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  line.setAttribute('fill', 'none');
  line.setAttribute('stroke', '#0057d9');
  line.setAttribute('stroke-width', '6');
  line.setAttribute('stroke-linecap', 'round');
  line.setAttribute('stroke-linejoin', 'round');
  line.setAttribute('opacity', '0.98');

  svg.append(outline, line);
  container.appendChild(svg);

  const overlay = {
    svg,
    outline,
    line,
    geometry: null,
    points: [],
    rafId: 0,
  };
  routeOverlays.set(targetMap, overlay);

  const schedule = () => {
    if (overlay.rafId) return;
    overlay.rafId = requestAnimationFrame(() => {
      overlay.rafId = 0;
      redrawOverlay(targetMap);
    });
  };

  targetMap.on('render', schedule);
  targetMap.on('resize', schedule);
  targetMap.on('move', schedule);
  targetMap.on('zoom', schedule);
  targetMap.on('rotate', schedule);
  targetMap.on('pitch', schedule);

  return overlay;
}

function redrawOverlay(targetMap) {
  const overlay = routeOverlays.get(targetMap);
  if (!overlay) return;

  const container = targetMap.getContainer();
  const width = Math.max(1, container.clientWidth);
  const height = Math.max(1, container.clientHeight);
  overlay.svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

  const coordinates = overlay.geometry?.coordinates || [];
  if (coordinates.length < 2) {
    overlay.outline.setAttribute('d', '');
    overlay.line.setAttribute('d', '');
    return;
  }

  const pathData = projectedPath(targetMap, coordinates);
  overlay.outline.setAttribute('d', pathData);
  overlay.line.setAttribute('d', pathData);
}

function projectedPath(targetMap, coordinates) {
  // Bardzo długie geometrie ograniczamy do rozsądnej liczby punktów, ale
  // zachowujemy początek, koniec i przebieg wszystkich zakrętów w skali mapy.
  const maxPoints = 12000;
  const step = coordinates.length > maxPoints
    ? Math.ceil(coordinates.length / maxPoints)
    : 1;

  const commands = [];
  for (let index = 0; index < coordinates.length; index += step) {
    const coordinate = coordinates[index];
    const point = targetMap.project([Number(coordinate[0]), Number(coordinate[1])]);
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) continue;
    commands.push(`${commands.length ? 'L' : 'M'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`);
  }

  const last = coordinates.at(-1);
  if (last) {
    const point = targetMap.project([Number(last[0]), Number(last[1])]);
    if (Number.isFinite(point.x) && Number.isFinite(point.y)) {
      commands.push(`L${point.x.toFixed(1)} ${point.y.toFixed(1)}`);
    }
  }

  return commands.join(' ');
}

function resolveRoadGeometry(route, points) {
  const direct = geometryCoordinates(route?.routeGeometry);
  if (direct.length >= 2) {
    return { type: 'LineString', coordinates: direct };
  }

  const legCoordinates = mergeGeometryCoordinates(route?.routeLegs);
  if (legCoordinates.length >= 2) {
    return { type: 'LineString', coordinates: legCoordinates };
  }

  const sectionCoordinates = mergeGeometryCoordinates(route?.sections);
  if (sectionCoordinates.length >= 2) {
    return { type: 'LineString', coordinates: sectionCoordinates };
  }

  const warningKey = points
    .filter(validCoordinate)
    .map(point => `${Number(point.lon).toFixed(5)},${Number(point.lat).toFixed(5)}`)
    .join('|');
  if (warningKey && !missingGeometryWarnings.has(warningKey)) {
    missingGeometryWarnings.add(warningKey);
    if (missingGeometryWarnings.size > 100) {
      const oldest = missingGeometryWarnings.values().next().value;
      missingGeometryWarnings.delete(oldest);
    }
    console.warn('[D24 MAP] Brak zapisanej geometrii OSRM — pokazuję awaryjne połączenie punktów prostymi. Otwórz trasę, przelicz ją i zapisz ponownie, aby zachować przebieg drogi.');
  }
  return {
    type: 'LineString',
    coordinates: points
      .filter(validCoordinate)
      .map(point => [Number(point.lon), Number(point.lat)]),
  };
}

function mergeGeometryCoordinates(items) {
  if (!Array.isArray(items)) return [];
  const coordinates = [];

  for (const item of items) {
    const current = geometryCoordinates(item?.geometry || item?.routeGeometry || item);
    if (current.length < 2) continue;
    if (!coordinates.length) coordinates.push(...current);
    else coordinates.push(...current.slice(1));
  }

  return coordinates;
}

function geometryCoordinates(value) {
  let candidate = value;

  if (typeof candidate === 'string') {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return [];
    }
  }

  if (candidate?.type === 'Feature') candidate = candidate.geometry;

  let coordinates = [];
  if (candidate?.type === 'LineString' && Array.isArray(candidate.coordinates)) {
    coordinates = candidate.coordinates;
  } else if (candidate?.type === 'MultiLineString' && Array.isArray(candidate.coordinates)) {
    coordinates = candidate.coordinates.flatMap((line, index) => (
      index ? (Array.isArray(line) ? line.slice(1) : []) : (Array.isArray(line) ? line : [])
    ));
  }

  return coordinates
    .map(pair => [Number(pair?.[0]), Number(pair?.[1])])
    .filter(([lon, lat]) => (
      Number.isFinite(lon)
      && Number.isFinite(lat)
      && lon >= -180
      && lon <= 180
      && lat >= -90
      && lat <= 90
    ));
}

function fitBoundsOnMap(targetMap, points = [], geometry = null, options = {}) {
  if (!targetMap) return;
  const bounds = new maplibregl.LngLatBounds();

  const coordinates = geometryCoordinates(geometry);
  coordinates.forEach(([lon, lat]) => bounds.extend([lon, lat]));

  points.forEach(point => {
    if (validCoordinate(point)) bounds.extend([Number(point.lon), Number(point.lat)]);
  });

  if (!bounds.isEmpty()) targetMap.fitBounds(bounds, options);
}

function drawCanvasRoute(context, targetMap, coordinates, scaleX, scaleY) {
  const drawPath = () => {
    context.beginPath();
    let started = false;
    for (const coordinate of coordinates) {
      const projected = targetMap.project([Number(coordinate[0]), Number(coordinate[1])]);
      const x = projected.x * scaleX;
      const y = projected.y * scaleY;
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      if (!started) {
        context.moveTo(x, y);
        started = true;
      } else {
        context.lineTo(x, y);
      }
    }
  };

  context.save();
  context.lineCap = 'round';
  context.lineJoin = 'round';

  drawPath();
  context.strokeStyle = '#ffffff';
  context.lineWidth = 10 * Math.max(scaleX, scaleY);
  context.stroke();

  drawPath();
  context.strokeStyle = '#0057d9';
  context.lineWidth = 6 * Math.max(scaleX, scaleY);
  context.stroke();
  context.restore();
}

function drawCanvasMarkers(context, targetMap, points, scaleX, scaleY) {
  context.save();
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.font = `bold ${11 * Math.max(scaleX, scaleY)}px Arial`;

  points.forEach((point, index) => {
    const projected = targetMap.project([Number(point.lon), Number(point.lat)]);
    const x = projected.x * scaleX;
    const y = projected.y * scaleY;
    const radius = 13 * Math.max(scaleX, scaleY);

    context.beginPath();
    context.arc(x, y, radius, 0, Math.PI * 2);
    context.fillStyle = point.isBorder ? '#b54708' : isRouteConstraint(point) ? '#f4b400' : '#0052a5';
    context.fill();
    context.lineWidth = 2 * Math.max(scaleX, scaleY);
    context.strokeStyle = '#ffffff';
    context.stroke();
    context.fillStyle = '#ffffff';
    context.fillStyle = isRouteConstraint(point) ? '#172033' : '#ffffff';
    context.fillText(point.isBorder ? 'G' : isRouteConstraint(point) ? 'P' : String(index + 1), x, y + 0.5);
  });

  context.restore();
}

function waitForMapReady(targetMap) {
  if (targetMap.loaded() || targetMap.isStyleLoaded()) return Promise.resolve();
  return new Promise(resolve => {
    let finished = false;
    const done = () => {
      if (finished) return;
      finished = true;
      resolve();
    };
    targetMap.once('load', done);
    setTimeout(done, 5000);
  });
}

function waitForMapIdle(targetMap, timeout = 1800) {
  return new Promise(resolve => {
    let finished = false;
    const done = () => {
      if (finished) return;
      finished = true;
      resolve();
    };
    targetMap.once('idle', done);
    setTimeout(done, timeout);
  });
}

function routePoints(route) {
  return route?.expandedPoints?.length
    ? route.expandedPoints
    : (route?.originalPoints || []);
}


function isRouteConstraint(point) {
  return Boolean(
    point?.isRouteConstraint
    || point?.manualRouteConstraint
    || point?.pointType === 'route-via'
    || point?.sourceType === 'manual-route-via'
  );
}

function validCoordinate(point) {
  const lon = Number(point?.lon);
  const lat = Number(point?.lat);
  return Number.isFinite(lon)
    && Number.isFinite(lat)
    && lon >= -180
    && lon <= 180
    && lat >= -90
    && lat <= 90;
}

function clearMarkers(markerStore) {
  markerStore.forEach(marker => marker.remove());
  markerStore.length = 0;
}

function escapeHtml(value) {
  return String(value || '').replace(/[&<>'"]/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  })[character]);
}
